import React from 'react';
import { GridComponent, ColumnsDirective, ColumnDirective, Inject, Page, Sort, Filter, Selection } from '@syncfusion/ej2-react-grids';
import { Box, Typography, Stack } from '@mui/material';

// Feature components
import { SearchBar, EmptyState } from './components';
import { DateRangeFilter, PayerGroupFilter } from './filters';
import WriteOffCandidatesDownloadCsv from '../../commands/WriteOffCandidatesDownloadCsv';

// Feature hooks
import { useWriteOffCandidatesData, useSearchFilter, ProcessedAccount } from './hooks';

// Feature templates & styles
import { ageTemplate, patientTemplate, serviceDateTemplate, balanceTemplate, arBalanceTemplate } from './templates';
import { gridContainerStyles, secondaryIconColor } from './styles';

interface WriteOffCandidatesGridProps {
  onSelectAccount: (accountId: string) => void;
}

/** Search fields for client-side filtering */
const SEARCH_FIELDS: (keyof ProcessedAccount | string)[] = [
  'accountNumber',
  'patient.firstName',
  'patient.lastName',
  'primaryPayerInfo.payerName',
  'encounterInfo.serviceDate',
];

const WriteOffCandidatesGrid: React.FC<WriteOffCandidatesGridProps> = ({ onSelectAccount }) => {
  // Data fetching and server-side filtering
  const {
    candidates,
    processedData,
    isLoading,
    error,
    dateFilter,
    payerGroupFilter,
    hasActiveFilters,
    queryParams,
  } = useWriteOffCandidatesData();

  // Client-side search filtering
  const { searchTerm, setSearchTerm, clearSearch, filteredData, hasSearch } = useSearchFilter({
    data: processedData,
    searchFields: SEARCH_FIELDS,
  });

  // Loading state
  if (isLoading) {
    return <EmptyState type="loading" />;
  }

  // Error state
  if (error) {
    return <EmptyState type="error" />;
  }

  // No center selected
  if (!queryParams.centerId) {
    return <EmptyState type="no-center" />;
  }

  // No candidates and no active filters
  if (candidates.length === 0 && !hasActiveFilters) {
    return <EmptyState type="no-data" />;
  }

  return (
    <Box sx={{ width: '100%', backgroundColor: '#fff' }}>
      {/* Search Bar and Export Button */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            onClear={clearSearch}
            placeholder="Search by account, patient, payer, or service date..."
          />
          <WriteOffCandidatesDownloadCsv filters={queryParams} />
        </Stack>
      </Box>

      {/* Date Range Filter */}
      <Box sx={{ mb: 3 }}>
        <DateRangeFilter filter={dateFilter} label="Service Date Range" />
      </Box>

      {/* Payer Group Filter */}
      <Box sx={{ mb: 3 }}>
        <PayerGroupFilter filter={payerGroupFilter} label="Payer Group" />
      </Box>

      {/* Results Summary */}
      {hasSearch && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ color: secondaryIconColor }}>
            Showing {filteredData.length} of {processedData.length} candidates
            {searchTerm && ` matching "${searchTerm}"`}
          </Typography>
        </Box>
      )}

      {/* Empty state when filters result in no candidates */}
      {candidates.length === 0 && hasActiveFilters && <EmptyState type="no-results" />}

      {/* Grid */}
      {candidates.length > 0 && (
        <Box sx={gridContainerStyles}>
          <GridComponent
            dataSource={filteredData}
            allowPaging
            allowSorting
            pageSettings={{ pageSize: 20, pageSizes: [10, 20, 50, 100] }}
            selectionSettings={{ type: 'Single', mode: 'Row' }}
            recordClick={(args: any) => {
              onSelectAccount(args.rowData.id);
            }}
            enableHtmlSanitizer={false}
            height="600px"
            rowHeight={45}
            gridLines="Both"
          >
            <ColumnsDirective>
              <ColumnDirective
                field="accountNumber"
                headerText="Account"
                width="120"
                type="string"
                allowSorting
              />
              <ColumnDirective
                field="patient"
                headerText="Patient"
                width="150"
                template={patientTemplate}
                allowSorting
              />
              <ColumnDirective
                field="encounterInfo.serviceDate"
                headerText="Service Date"
                width="120"
                template={serviceDateTemplate}
                type="date"
                allowSorting
              />
              <ColumnDirective
                field="ageDays"
                headerText="Age"
                width="90"
                template={ageTemplate}
                allowSorting
                type="number"
              />
              <ColumnDirective
                field="balance"
                headerText="Balance"
                width="120"
                template={balanceTemplate}
                textAlign="Right"
                allowSorting
                type="number"
              />
              <ColumnDirective
                field="arBalance"
                headerText="AR Balance"
                width="120"
                template={arBalanceTemplate}
                textAlign="Right"
                allowSorting
                type="number"
              />
              <ColumnDirective
                field="primaryPayerInfo.payerName"
                headerText="Payer"
                width="150"
                allowSorting
              />
            </ColumnsDirective>
            <Inject services={[Page, Sort, Filter, Selection]} />
          </GridComponent>
        </Box>
      )}
    </Box>
  );
};

export default WriteOffCandidatesGrid;
